import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import Swal from 'sweetalert2';
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Button, Input, Typography, Spinner, Switch
} from '@material-tailwind/react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const REGEX_NOMBRE_USUARIO = /^[a-zA-Z0-9._-]{3,100}$/
const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const FORM_VACIO = {
    id_usuario: null,
    nombre: '',
    apellido: '',
    nombre_usuario: '',
    contraseña: '',
    sede_admin: true,
    id_region: '',
    rol_sede: '',
    tel_movil: '',
    email: '',
    activo: true,
}

export const AbmUsuario = ({ open, onCerrar, onGuardado, idUsuario, perfiles = [], regiones = [] }) => {

    const [form, setForm] = useState(FORM_VACIO)
    const [errores, setErrores] = useState({})
    const [guardando, setGuardando] = useState(false)
    const [cargando, setCargando] = useState(false)

    const esEdicion = Boolean(idUsuario)

    useEffect(() => {
        if (!open) return

        setErrores({})

        if (esEdicion) {
            cargarUsuario(idUsuario)
        } else {
            setForm(FORM_VACIO)
        }
    }, [open, idUsuario])

    const cargarUsuario = async (id) => {
        setCargando(true)
        try {
            const { data } = await Axios.get(`/api/tablasgenerales/usuarios/${id}`)

            setForm({
                id_usuario: data.id_usuario,
                nombre: data.nombre ?? '',
                apellido: data.apellido ?? '',
                nombre_usuario: data.nombre_usuario ?? '',
                contraseña: '',
                sede_admin: Boolean(data.sede_admin),
                id_region: data.id_region ?? '',
                rol_sede: data.rol_sede ?? '',
                tel_movil: data.tel_movil ?? '',
                email: data.email ?? '',
                activo: Boolean(data.activo),
            })
        } catch (err) {
            console.error('Error al cargar el usuario', err)
            Swal.fire('Error', 'No se pudieron cargar los datos del usuario', 'error')
            onCerrar && onCerrar()
        } finally {
            setCargando(false)
        }
    }

    const cambiar = (campo, valor) => {
        setForm(prev => ({ ...prev, [campo]: valor }))
        setErrores(prev => ({ ...prev, [campo]: undefined }))
    }

    const cambiarTipo = (esSede) => {
        setForm(prev => ({ ...prev, sede_admin: esSede, id_region: esSede ? '' : prev.id_region }))
        setErrores(prev => ({ ...prev, id_region: undefined }))
    }

    const validar = () => {
        const nuevos = {}

        if (!form.nombre.trim()) nuevos.nombre = 'El nombre es obligatorio'
        if (!form.apellido.trim()) nuevos.apellido = 'El apellido es obligatorio'

        const usuario = form.nombre_usuario.trim()
        if (!usuario) {
            nuevos.nombre_usuario = 'El nombre de usuario es obligatorio'
        } else if (!REGEX_NOMBRE_USUARIO.test(usuario)) {
            nuevos.nombre_usuario = 'Solo letras, números, punto, guion y guion bajo. Sin espacios (mínimo 3 caracteres)'
        }

        if (!form.tel_movil.trim()) nuevos.tel_movil = 'El teléfono móvil es obligatorio'

        const email = form.email.trim()
        if (!email) {
            nuevos.email = 'El email es obligatorio'
        } else if (!REGEX_EMAIL.test(email)) {
            nuevos.email = 'El formato del email no es válido'
        }

        if (form.rol_sede === '' || form.rol_sede === null) nuevos.rol_sede = 'Debe seleccionar un perfil'

        if (!form.sede_admin && (form.id_region === '' || Number(form.id_region) === 0)) {
            nuevos.id_region = 'Un usuario regional debe tener una región asignada'
        }

        setErrores(nuevos)
        return Object.keys(nuevos).length === 0
    }

    const guardar = async () => {
        if (!validar()) return

        const payload = {
            nombre: form.nombre.trim(),
            apellido: form.apellido.trim(),
            nombre_usuario: form.nombre_usuario.trim(),
            contraseña: form.contraseña ? form.contraseña : null,
            sede_admin: form.sede_admin,
            id_region: form.sede_admin ? null : Number(form.id_region),
            rol_sede: Number(form.rol_sede),
            activo: form.activo,
            tel_movil: form.tel_movil.trim(),
            email: form.email.trim().toLowerCase(),
        }

        setGuardando(true)
        try {
            if (esEdicion) {
                await Axios.put(`/api/tablasgenerales/usuarios/${form.id_usuario}`, payload)
            } else {
                await Axios.post('/api/tablasgenerales/usuarios', payload)
            }

            Swal.fire({
                icon: 'success',
                title: esEdicion ? 'Usuario actualizado' : 'Usuario creado',
                timer: 2000,
                showConfirmButton: false
            })

            onGuardado && onGuardado()
            onCerrar && onCerrar()
        } catch (error) {
            console.error('Error al guardar el usuario', error)
            const detalle = error?.response?.data?.message || error?.response?.data || error.message
            Swal.fire('No se pudo guardar', detalle || 'Ocurrió un error al guardar el usuario', 'error')
        } finally {
            setGuardando(false)
        }
    }

    const campoTexto = (label, campo, opts = {}) => (
        <div className="flex flex-col gap-1">
            <Typography variant="small" color="blue-gray" className="font-medium">
                {label}{opts.obligatorio !== false && <span className="text-red-500 ml-1">*</span>}
            </Typography>
            <Input
                type={opts.type || 'text'}
                color="black"
                value={form[campo] ?? ''}
                maxLength={opts.maxLength || 100}
                placeholder={opts.placeholder}
                onChange={(e) => cambiar(campo, e.target.value)}
                className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                labelProps={{ className: "before:content-none after:content-none" }}
            />
            {errores[campo] && <span className="text-xs text-red-500">{errores[campo]}</span>}
            {opts.ayuda && !errores[campo] && <span className="text-xs text-gray-400">{opts.ayuda}</span>}
        </div>
    )

    return (
        <Dialog
            open={open}
            handler={() => onCerrar && onCerrar()}
            size="lg"
            className="m-0 w-[95vw] min-w-0 max-w-[95vw] md:w-auto md:max-w-[48rem] max-h-[92vh] overflow-hidden"
        >
            <DialogHeader className="text-lg md:text-xl">
                {esEdicion ? `Editar usuario #${idUsuario}` : 'Nuevo usuario'}
            </DialogHeader>

            <DialogBody className="max-h-[65vh] overflow-y-auto px-4 md:px-6">
                {cargando ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Spinner className="h-8 w-8" />
                        <span className="mt-3 text-sm text-gray-500">Cargando datos...</span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-5">

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {campoTexto('Nombre', 'nombre')}
                            {campoTexto('Apellido', 'apellido')}
                            {campoTexto('Nombre de usuario', 'nombre_usuario', { ayuda: 'Sin espacios. Se valida que no exista otro igual.' })}
                            {campoTexto('Contraseña', 'contraseña', { type: 'password', obligatorio: false, ayuda: 'Opcional. Si se deja vacío la genera el sistema.' })}
                        </div>

                        <div className="flex flex-col gap-2 bg-gray-50 border border-gray-200 rounded-md p-4">
                            <Typography variant="small" color="blue-gray" className="font-medium">
                                Tipo de usuario<span className="text-red-500 ml-1">*</span>
                            </Typography>
                            <div className="flex flex-wrap gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="tipo_usuario" checked={form.sede_admin === true} onChange={() => cambiarTipo(true)} className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm text-gray-700">Sede administrativa</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="tipo_usuario" checked={form.sede_admin === false} onChange={() => cambiarTipo(false)} className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm text-gray-700">Secretario regional</span>
                                </label>
                            </div>
                            <span className="text-xs text-gray-400">
                                Sede administrativa no lleva región. Secretario regional requiere una región.
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="font-medium">
                                    Región{!form.sede_admin && <span className="text-red-500 ml-1">*</span>}
                                </Typography>
                                <select
                                    className="select-bg-ui w-full h-10 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    value={form.id_region ?? ''}
                                    disabled={form.sede_admin}
                                    onChange={(e) => cambiar('id_region', e.target.value)}
                                >
                                    <option value="">{form.sede_admin ? 'No aplica (sede administrativa)' : 'Seleccione una región...'}</option>
                                    {regiones.map(item => (
                                        <option key={item.id_region} value={item.id_region}>{item.nombre ?? item.id_region}</option>
                                    ))}
                                </select>
                                {errores.id_region && <span className="text-xs text-red-500">{errores.id_region}</span>}
                            </div>

                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="font-medium">
                                    Perfil<span className="text-red-500 ml-1">*</span>
                                </Typography>
                                <select
                                    className="select-bg-ui w-full h-10"
                                    value={form.rol_sede ?? ''}
                                    onChange={(e) => cambiar('rol_sede', e.target.value)}
                                >
                                    <option value="">Seleccione un perfil...</option>
                                    {perfiles.map(item => (
                                        <option key={item.id_usuario_perfil ?? item.nro_perfil} value={item.nro_perfil} title={item.descripcion}>
                                            {item.nombre}
                                        </option>
                                    ))}
                                </select>
                                {errores.rol_sede && <span className="text-xs text-red-500">{errores.rol_sede}</span>}
                                {perfiles.length === 0 && <span className="text-xs text-amber-600">No se pudieron cargar los perfiles</span>}
                            </div>

                            {campoTexto('Teléfono móvil', 'tel_movil')}
                            {campoTexto('Email', 'email', { type: 'email' })}
                        </div>

                        <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-md p-4">
                            <Switch
                                ripple={false}
                                checked={form.activo}
                                onChange={(e) => cambiar('activo', e.target.checked)}
                            />
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-700">Usuario activo</span>
                                <span className="text-xs text-gray-500">Si se desactiva, el usuario no podrá ingresar al sistema.</span>
                            </div>
                        </div>
                    </div>
                )}
            </DialogBody>

            <DialogFooter className="flex flex-wrap justify-end gap-2 px-4 md:px-6">
                <Button variant="text" color="blue-gray" onClick={() => onCerrar && onCerrar()} disabled={guardando}>
                    <XMarkIcon className="h-4 w-4 mr-1 inline" />Cancelar
                </Button>
                <Button variant="gradient" color="blue" onClick={guardar} disabled={guardando || cargando} className="flex items-center gap-2">
                    {guardando ? <Spinner className="h-4 w-4" /> : <CheckIcon className="h-4 w-4" />}
                    {esEdicion ? 'Guardar cambios' : 'Crear usuario'}
                </Button>
            </DialogFooter>
        </Dialog>
    )
}
